import { Asset } from "models/Asset";
import { Account } from "models/Account";
import { DashboardWidgetType } from "models/Dashboard";
import { Project } from "models/Project";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as api from "services/Api";
import { getOrphanedPayments, getInvoices } from "store/actions/Billing";
import { getCalls } from "store/actions/Calls";
import { getCustomers, getPersons } from "store/actions/Customers";
import { getUnreadEmails } from "store/actions/Emails";
import { getExpenses } from "store/actions/Expenses";

export interface DashboardDataValue {
    accounts: Account[];
    accountsFailed: boolean;
    accountsLoading: boolean;
    assets: Asset[];
    assetsFailed: boolean;
    assetsLoading: boolean;
    projects: Project[];
    projectsFailed: boolean;
    projectsLoading: boolean;
}

export const DashboardDataContext = createContext<DashboardDataValue>({
    accounts: [],
    accountsFailed: false,
    accountsLoading: false,
    assets: [],
    assetsFailed: false,
    assetsLoading: false,
    projects: [],
    projectsFailed: false,
    projectsLoading: false,
});

export const useDashboardData = () => useContext(DashboardDataContext);

interface Props {
    children: React.ReactNode;
    widgetTypes: DashboardWidgetType[];
}

interface Actions {
    getCalls: () => unknown;
    getCustomers: () => unknown;
    getExpenses: () => unknown;
    getInvoices: () => unknown;
    getOrphanedPayments: () => unknown;
    getPersons: () => unknown;
    getUnreadEmails: () => unknown;
}

export const DashboardDataProviderBase: React.FC<Props & Actions> = ({
    children,
    widgetTypes,
    getCalls,
    getCustomers,
    getExpenses,
    getInvoices,
    getOrphanedPayments,
    getPersons,
    getUnreadEmails,
}) => {
    const loaded = useRef(new Set<string>());
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [accountsFailed, setAccountsFailed] = useState(false);
    const [projects, setProjects] = useState<Project[]>([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [projectsFailed, setProjectsFailed] = useState(false);
    const [assets, setAssets] = useState<Asset[]>([]);
    const [assetsLoading, setAssetsLoading] = useState(false);
    const [assetsFailed, setAssetsFailed] = useState(false);
    const types = useMemo(() => new Set(widgetTypes), [widgetTypes]);

    useEffect(() => {
        const loadOnce = (key: string, load: () => unknown) => {
            if (loaded.current.has(key)) return;
            loaded.current.add(key);
            load();
        };
        if (types.has("sales_analytics") || types.has("revenue") || types.has("outstanding") || types.has("open_invoices")) {
            loadOnce("invoices", getInvoices);
        }
        if (types.has("sales_analytics")) {
            loadOnce("expenses", getExpenses);
            loadOnce("customers", getCustomers);
        }
        if (types.has("orphaned_payments")) loadOnce("orphaned_payments", getOrphanedPayments);
        if (types.has("recent_calls")) {
            loadOnce("calls", getCalls);
            loadOnce("persons", getPersons);
        }
        if (types.has("unread_email")) loadOnce("unread_email", getUnreadEmails);
    }, [getCalls, getCustomers, getExpenses, getInvoices, getOrphanedPayments, getPersons, getUnreadEmails, types]);

    useEffect(() => {
        if (!(types.has("active_projects") || types.has("pending_hours") || types.has("worker_client_balance")) || loaded.current.has("projects")) return;
        loaded.current.add("projects");
        setProjectsLoading(true);
        api.getProjects()
            .then(setProjects)
            .catch(() => setProjectsFailed(true))
            .finally(() => setProjectsLoading(false));
    }, [types]);

    useEffect(() => {
        if (!(types.has("asset_services") || types.has("upcoming_asset_services")) || loaded.current.has("assets")) return;
        loaded.current.add("assets");
        setAssetsLoading(true);
        api.getAssets()
            .then(setAssets)
            .catch(() => setAssetsFailed(true))
            .finally(() => setAssetsLoading(false));
    }, [types]);

    useEffect(() => {
        const needsAccounts = types.has("pending_hours")
            || types.has("pending_documents")
            || types.has("open_invoices")
            || types.has("worker_client_balance");
        if (!needsAccounts || loaded.current.has("accounts")) return;
        loaded.current.add("accounts");
        setAccountsLoading(true);
        api.getAccounts()
            .then(setAccounts)
            .catch(() => setAccountsFailed(true))
            .finally(() => setAccountsLoading(false));
    }, [types]);

    const value = useMemo<DashboardDataValue>(() => ({
        accounts,
        accountsFailed,
        accountsLoading,
        assets,
        assetsFailed,
        assetsLoading,
        projects,
        projectsFailed,
        projectsLoading,
    }), [accounts, accountsFailed, accountsLoading, assets, assetsFailed, assetsLoading, projects, projectsFailed, projectsLoading]);

    return <DashboardDataContext.Provider value={value}>{children}</DashboardDataContext.Provider>;
};

const mapDispatchToProps = (dispatch: any) => bindActionCreators({
    getCalls,
    getCustomers,
    getExpenses,
    getInvoices,
    getOrphanedPayments,
    getPersons,
    getUnreadEmails,
}, dispatch);

export default connect(null, mapDispatchToProps)(DashboardDataProviderBase);
